import { useState, useEffect, useCallback, useRef } from "react";
import Plot from "react-plotly.js";
import {
  PlusIcon, TrashIcon, ChevronDownIcon, ChevronRightIcon,
  ClipboardDocumentIcon, ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import { statCurvesApi } from "../../api/statCurves";
import theme from "../../themes";

const VARIANTS = ["standard", "progressive", "conservative"];

function bhHex(shade) {
  const rgb = theme.colors[shade];
  if (!rgb) return "#888";
  const [r, g, b] = rgb.split(" ").map(Number);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

const VARIANT_META = {
  standard:     { label: "Standard",     color: bhHex(600) },
  progressive:  { label: "Progressive",  color: bhHex(400) },
  conservative: { label: "Conservative", color: bhHex(800) },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function emptyPoints() {
  return [{ x: "", y: "" }];
}

function parsePoints(raw) {
  if (!raw || raw.length === 0) return emptyPoints();
  return raw.map((p) => ({ x: String(p.x), y: String(p.y) }));
}

function toNumberPoints(rows) {
  return rows
    .filter((r) => r.x !== "" && r.y !== "")
    .map((r) => ({ x: parseFloat(r.x), y: parseFloat(r.y) }))
    .filter((p) => !isNaN(p.x) && !isNaN(p.y));
}

// ── Point table ───────────────────────────────────────────────────────────────

function PointTable({ variant, rows, onChange, xLabel, yLabel }) {
  const { label, color } = VARIANT_META[variant];
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);

  const setCell = (i, key, val) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));

  const addRow = () => onChange([...rows, { x: "", y: "" }]);

  const removeRow = (i) => {
    const next = rows.filter((_, idx) => idx !== i);
    onChange(next.length ? next : emptyPoints());
  };

  const copyToClipboard = useCallback(() => {
    const text = rows
      .filter((r) => r.x !== "" || r.y !== "")
      .map((r) => `${r.x}\t${r.y}`)
      .join("\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 1500);
    });
  }, [rows]);

  const handlePaste = useCallback((e) => {
    const text = e.clipboardData.getData("text");
    if (!text.includes("\t") && !text.includes("\n")) return;
    e.preventDefault();
    const parsed = text
      .trim()
      .split(/\r?\n/)
      .map((line) => {
        const parts = line.split("\t");
        return {
          x: (parts[0] ?? "").replace(",", "."),
          y: (parts[1] ?? "").replace(",", "."),
        };
      })
      .filter((r) => r.x !== "" || r.y !== "");
    if (parsed.length) onChange(parsed);
  }, [onChange]);

  return (
    <div className="flex-1 min-w-0" onPaste={handlePaste}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="w-6 border border-gray-200 px-1 py-1 text-gray-400 font-normal">#</th>
              <th className="border border-gray-200 px-2 py-1 text-gray-600 font-semibold text-left">{xLabel}</th>
              <th className="border border-gray-200 px-2 py-1 text-gray-600 font-semibold text-left">{yLabel}</th>
              <th className="w-6 border border-gray-200 px-1 text-center">
                <button
                  onClick={copyToClipboard}
                  title="Copy table to clipboard"
                  className="text-gray-300 hover:text-bh-600 transition-colors"
                >
                  {copied
                    ? <ClipboardDocumentCheckIcon className="h-3.5 w-3.5 text-bh-500" />
                    : <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                  }
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-bh-50/40">
                <td className="border border-gray-200 px-1 py-0.5 text-gray-300 text-center select-none">{i + 1}</td>
                <td className="border border-gray-200 p-0">
                  <input
                    type="number"
                    step="any"
                    value={row.x}
                    onChange={(e) => setCell(i, "x", e.target.value)}
                    className="w-full px-2 py-1 text-xs bg-transparent focus:outline-none focus:bg-bh-50"
                  />
                </td>
                <td className="border border-gray-200 p-0">
                  <input
                    type="number"
                    step="any"
                    value={row.y}
                    onChange={(e) => setCell(i, "y", e.target.value)}
                    className="w-full px-2 py-1 text-xs bg-transparent focus:outline-none focus:bg-bh-50"
                  />
                </td>
                <td className="border border-gray-200 px-1 text-center">
                  <button
                    onClick={() => removeRow(i)}
                    className="text-gray-300 hover:text-red-400 transition-colors"
                  >
                    <TrashIcon className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        onClick={addRow}
        className="mt-1 flex items-center gap-1 text-xs text-bh-500 hover:text-bh-700 px-1"
      >
        <PlusIcon className="h-3 w-3" />
        Add row
      </button>
      <p className="text-gray-400 text-xs mt-1 px-1">
        Copy table header icon → paste with Ctrl+V in another table
      </p>
    </div>
  );
}

// ── Plotly preview ────────────────────────────────────────────────────────────

function CurveChart({ family, tableData }) {
  const traces = VARIANTS.map((v) => {
    const pts = toNumberPoints(tableData[v] || []).sort((a, b) => a.x - b.x);
    return {
      x:    pts.map((p) => p.x),
      y:    pts.map((p) => p.y),
      name: VARIANT_META[v].label,
      mode: "lines",
      type: "scatter",
      line: { color: VARIANT_META[v].color, width: 2 },
    };
  });

  return (
    <div style={{ height: "300px" }}>
      <Plot
        data={traces}
        layout={{
          autosize: true,
          margin: { t: 10, r: 20, b: 50, l: 55 },
          xaxis: {
            title: { text: `${family.x_param}${family.x_unit ? ` [${family.x_unit}]` : ""}`, font: { size: 12 } },
            gridcolor: "#e5e7eb",
            zerolinecolor: "#d1d5db",
          },
          yaxis: {
            title: { text: family.y_param, font: { size: 12 } },
            gridcolor: "#e5e7eb",
            zerolinecolor: "#d1d5db",
          },
          legend:        { orientation: "h", x: 0, y: 1.08, font: { size: 11 } },
          plot_bgcolor:  "#ffffff",
          paper_bgcolor: "#ffffff",
          font:          { family: "Inter, sans-serif", size: 11, color: "#374151" },
        }}
        config={{ displayModeBar: false, responsive: true }}
        style={{ width: "100%", height: "100%" }}
        useResizeHandler
      />
    </div>
  );
}

// ── Family section ────────────────────────────────────────────────────────────

function FamilySection({ family, onUpdated }) {
  const [open,      setOpen]      = useState(false);
  const [tableData, setTableData] = useState({});
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [error,     setError]     = useState(null);
  const savedTimer = useRef(null);

  // initialise tables from family data
  useEffect(() => {
    const init = {};
    VARIANTS.forEach((v) => {
      init[v] = parsePoints(family.curves?.[v]?.points ?? []);
    });
    setTableData(init);
  }, [family]);

  const setVariantRows = (variant) => (rows) =>
    setTableData((prev) => ({ ...prev, [variant]: rows }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const results = await Promise.all(
        VARIANTS.map((v) =>
          statCurvesApi.adminSaveCurve(family.id, v, toNumberPoints(tableData[v] || []))
        )
      );
      // rebuild family.curves from responses
      const updatedCurves = {};
      results.forEach((r) => { updatedCurves[r.data.variant] = r.data; });
      onUpdated({ ...family, curves: updatedCurves });

      setSaved(true);
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err?.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {open
            ? <ChevronDownIcon  className="h-4 w-4 text-gray-400 flex-shrink-0" />
            : <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
          }
          <span className="font-semibold text-gray-900">{family.display_name}</span>
          <div className="flex flex-col gap-0.5">
            {family.x_formula
              ? <span className="text-xs text-gray-400 font-mono">{family.x_formula}</span>
              : <span className="text-xs text-gray-400 font-mono">{family.x_param}{family.x_unit ? ` [${family.x_unit}]` : ""}</span>
            }
            {family.y_formula
              ? <span className="text-xs text-gray-500 font-mono">{family.y_formula}</span>
              : <span className="text-xs text-gray-400 font-mono">{family.y_param}{family.y_unit && family.y_unit !== "-" ? ` [${family.y_unit}]` : ""}</span>
            }
          </div>
        </div>
      </button>

      {/* Body */}
      {open && (
        <div className="border-t border-gray-100 p-5 space-y-5">
          {/* Chart */}
          <div className="border border-gray-100 rounded-lg overflow-hidden bg-white">
            <CurveChart family={family} tableData={tableData} />
          </div>

          {/* Tables */}
          <div className="flex gap-4">
            {VARIANTS.map((v) => (
              <PointTable
                key={v}
                variant={v}
                rows={tableData[v] || emptyPoints()}
                onChange={setVariantRows(v)}
                xLabel={family.x_unit ? `${family.x_param} [${family.x_unit}]` : family.x_param}
                yLabel={family.y_unit && family.y_unit !== "-" ? `${family.y_param} [${family.y_unit}]` : family.y_param}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-bh-600 text-white rounded-lg text-sm font-medium hover:bg-bh-700 disabled:opacity-50 transition-colors"
            >
              {saving ? "Saving…" : "Save curves"}
            </button>
            {saved && (
              <span className="text-xs text-green-600 font-medium">Saved</span>
            )}
            {error && (
              <span className="text-xs text-red-600">{error}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AdminStatCurvesPage() {
  const [families, setFamilies] = useState([]);
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await statCurvesApi.adminList();
      setFamilies(data);
    } catch {
      setFamilies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpdated = (updated) =>
    setFamilies((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));

  return (
    <div className="p-6 max-w-6xl space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Statistical Curves</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Dimensionless hydraulic reference curves used for pre-design parameter estimation.
          Enter (nq, y) point pairs — curves are stored as linear polylines.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : families.length === 0 ? (
        <p className="text-sm text-gray-400">No curve families found.</p>
      ) : (
        <div className="space-y-3">
          {families.map((f) => (
            <FamilySection key={f.id} family={f} onUpdated={handleUpdated} />
          ))}
        </div>
      )}
    </div>
  );
}
